<template>
  <section
    class="relative min-h-[70vh] sm:min-h-[85vh] flex items-center justify-center overflow-hidden"
    aria-label="Hero"
  >
    <div class="absolute inset-0 z-0">
      <div class="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-t3-bg-dark" aria-hidden="true" />
      <img
        v-if="bannerUrl"
        :src="bannerUrl"
        :alt="name ? `${name} — restaurant` : 'Restaurant'"
        class="w-full h-full object-cover"
        loading="eager"
      >
      <div v-else class="w-full h-full bg-t3-accent-dark" />
    </div>
    <div class="relative z-10 text-center px-4 py-12 sm:py-16">
      <div v-if="logoUrl && canOpenOwnerLogoModal" class="flex justify-center mb-6 sm:mb-8">
        <button
          type="button"
          class="inline-flex rounded-lg border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
          :aria-label="$t('public.ownerEditLogoAria')"
          data-testid="public-hero-logo"
          @click="openOwnerLogoModal"
        >
          <img
            :src="logoUrl"
            :alt="name || 'Restaurant'"
            class=" h-24 sm:h-32 rounded-lg object-contain border-2 border-white/20 shadow-lg"
          >
        </button>
      </div>
      <div v-else-if="logoUrl" class="flex justify-center mb-6 sm:mb-8" data-testid="public-hero-logo">
        <img
          :src="logoUrl"
          :alt="name || 'Restaurant'"
          class=" h-24 sm:h-32 rounded-lg object-contain border-2 border-white/20 shadow-lg"
        >
      </div>
      <div v-else-if="viewer?.is_owner && ownerViewMode && canOpenOwnerLogoModal" class="flex justify-center mb-6 sm:mb-8">
        <button
          type="button"
          class="max-w-[200px] rounded border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
          :aria-label="$t('public.ownerEditLogoAria')"
          @click="openOwnerLogoModal"
        >
          <div class="py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
            <p class="text-sm font-mono text-slate-300">{{ $t('public.noLogoSetOwnerMessage') }}</p>
          </div>
        </button>
      </div>
      <div v-else-if="viewer?.is_owner && ownerViewMode" class="flex justify-center mb-6 sm:mb-8">
        <div class="max-w-[200px] py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
          <p class="text-sm font-mono text-slate-300">{{ $t('public.noLogoSetOwnerMessage') }}</p>
        </div>
      </div>
      <div class="flex justify-center mb-4 sm:mb-6 w-full">
        <PublicOpenStatusBadge :operating-hours="operatingHours" variant="t3-hero" />
      </div>
      <h1 class="font-t3-display font-light text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl mb-4 sm:mb-6 tracking-tight">
        {{ heroTitle }}
      </h1>
      <p v-if="heroSubtext" class="text-slate-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 font-light italic">
        {{ heroSubtext }}
      </p>
      <div v-else-if="viewer?.is_owner && ownerViewMode" class="max-w-md mx-auto mb-8 sm:mb-10 py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
        <p class="text-sm font-mono text-slate-300">{{ $t('public.noTaglineOrDescriptionSetOwnerMessage') }}</p>
      </div>
      <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center flex-wrap">
        <a
          href="#menu"
          class="bg-t3-primary text-t3-bg-dark px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base hover:scale-105 transition-transform min-h-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
        >
          {{ viewListLabel || $t('public.viewMenu') }}
        </a>
        <a
          v-if="hasAbout"
          href="#about"
          class="border border-white/30 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base hover:bg-white/10 transition-colors min-h-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
        >
          {{ $t('public.t3OurStory') }}
        </a>
        <div
          v-else-if="viewer?.is_owner && ownerViewMode"
          class="py-3 px-4 rounded-lg border border-dashed border-white/30 bg-white/10 max-w-[200px]"
        >
          <p class="text-xs font-mono text-slate-300">{{ $t('public.ownerNavAboutUnavailable') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()

const props = defineProps({
  name: { type: String, default: '' },
  tagline: { type: String, default: '' },
  description: { type: String, default: '' },
  bannerUrl: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  operatingHours: { type: Object, default: null },
  viewListLabel: { type: String, default: '' },
  hasAbout: { type: Boolean, default: false },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
})

const heroTitle = computed(() => (props.name || 'Culinary Artistry').trim() || 'Culinary Artistry')
const heroSubtext = computed(() => (props.tagline || props.description || '').trim())
</script>
